import type { ReactNode, VoidFunctionComponent } from 'react';

import { useGitHubAppInstallation } from '../../../../hooks/api/vcs/githubApp/useGitHubAppInstallation';
import { useStartGitHubAppInstall } from '../../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { getErrorMessage } from '../../../../ReactQuery/errors';
import DetailsPanel from '../../../generic/DetailsPanel';
import VulnerabilityDrawerSection from '../../../shared/vulns/Drawer/Section';

import { FossabotConnectionBanner } from './FossabotConnectionBanner';
import { FossabotCreateTicketBox } from './FossabotCreateTicketBox';
import { FossabotFixBox } from './FossabotFixBox';
import { FossabotNoFixBox } from './FossabotNoFixBox';
import { FossabotOtherFixesBox } from './FossabotOtherFixesBox';
import { useFossabotNoFix } from './useFossabotNoFix';
import { useFossabotPrFlow } from './useFossabotPrFlow';

/**
 * The fossabot remediation panel, shown for vulnerability issues when the
 * fossabotDepUpgradePRs flag is on and the user can view fossabot upgrades.
 * Renders the collapsible Remediation section with the fossabot connection
 * banner bleeding edge to edge across the panel.
 */
export const FossabotRemediationPanel: VoidFunctionComponent = () => {
  const { data, isLoading, isError, error } = useGitHubAppInstallation('fossabot');
  const { mutate: startInstall } = useStartGitHubAppInstall();

  // !isError guards a stale-success refetch: react-query keeps the last
  // successful `data` around while a background refetch errors.
  const isConnected = Boolean(data?.installed) && !isError;
  const noFixType = useFossabotNoFix();
  const prFlow = useFossabotPrFlow(isConnected);

  /*
    While the connection status is loading, render no box; it resolves in a
    moment and the banner already covers that state. With no fix to offer,
    neither box has anything to show, so both give way to the unfixable state.
    Not connected, or the status check failed and we cannot tell: the
    create-ticket upgrade options, which do not need fossabot at all.
  */
  const renderBox = (): ReactNode => {
    if (isLoading) {
      return null;
    }
    if (noFixType !== null) {
      return <FossabotNoFixBox type={noFixType} />;
    }
    if (isConnected) {
      return (
        <>
          {prFlow && (
            <FossabotFixBox
              status={prFlow.status}
              vcsUrl={prFlow.vcsUrl}
              fossabotUrl={prFlow.fossabotUrl}
              onCreatePR={prFlow.onCreatePR}
              errorMessage={prFlow.errorMessage}
              onRetry={prFlow.onRetry}
            />
          )}
          <FossabotOtherFixesBox />
        </>
      );
    }
    return <FossabotCreateTicketBox />;
  };

  return (
    <VulnerabilityDrawerSection>
      <DetailsPanel id="fossabot-remediation" target="Remediation" isUnpadded>
        <div data-testid="fossabot-remediation-panel">
          <FossabotConnectionBanner
            isConnected={isConnected}
            isLoading={isLoading}
            isError={isError}
            errorMessage={getErrorMessage(error, 'Could not check fossabot connection status.')}
            onConnect={() => startInstall('fossabot')}
          />
          {renderBox()}
        </div>
      </DetailsPanel>
    </VulnerabilityDrawerSection>
  );
};
